import { renderTemplate } from "../templateRenderer";
import { COURIER_PARCEL_TEMPLATE, COURIER_PARCEL_CSS } from "./loader";
import { IMAGE_URL } from "../../../Constant";
import type { PageSizeType } from "../types";

export interface CourierParcelContext {
  storeName: string;
  address: string;
  phone: string;
  email: string;
  whatsapp: string;
  website: string;
  logoUrl: string;
  footer: string;
  orderNo: string;
  dateStr: string;
  customerName: string;
  customerPhone: string;
  customerAddress: string;
  customerEmail: string;
  description: string;
  isFragile: boolean;
  showCustomer: boolean;
  items: any[];
  totalItems: number;
  returnAddress: string;
  altDeliveryAddress: string;
  deliveryInstructions: string;
  preferredDeliveryTime: string;
  thisSideUp: boolean;
  keepDry: boolean;
  doNotStack: boolean;
  perishable: boolean;
  hazardous: boolean;
  customHandling: string;
  codAmount: number;
  paidAmount: number;
  deliveryZone: string;
  returnAddressNote: string;
  trackingCode: string;
  serviceType: string;
  courierName: string;
  courierPhone: string;
  paidAmountFormatted: string;
  codAmountFormatted: string;
  isA4: boolean;
  pageSize: PageSizeType;
}

const PAGE_SIZES: Record<PageSizeType, { width: string; pad: string; font: string }> = {
  A4: { width: "210mm", pad: "10mm 12mm", font: "13px" },
  A3: { width: "297mm", pad: "14mm 16mm", font: "15px" },
  "80mm": { width: "72mm", pad: "4mm", font: "11px" },
};

const BASE_TEMPLATE = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8"/>
  <meta name="viewport" content="width=device-width,initial-scale=1"/>
  <title>Courier Parcel - {{ orderNo }}</title>
  <style>{{ styles }}</style>
</head>
<body>
  {{ content | safe }}
</body>
</html>`;

function buildCssVariables(pageSize: PageSizeType): string {
  const is80 = pageSize === "80mm";
  const sz = PAGE_SIZES[pageSize];
  return `
    :root {
      --cp-font:       ${sz.font};
      --cp-width:      ${sz.width};
      --cp-pad:        ${sz.pad};
      --cp-hdr-gap:    ${is80 ? "8px" : "20px"};
      --cp-hdr-size:   ${is80 ? "1.1em" : "1.5em"};
      --cp-hdr-sub:    ${is80 ? "0.78em" : "0.9em"};
      --cp-body-size:  ${is80 ? "0.82em" : "0.95em"};
      --cp-table-size: ${is80 ? "0.8em" : "0.95em"};
      --cp-ftr-size:   ${is80 ? "0.72em" : "0.9em"};
      --cp-page-size:  ${is80 ? "80mm auto" : pageSize};
    }
  `;
}

export function renderCourierParcel(ctx: CourierParcelContext): string {
  const logoUrl = ctx.logoUrl
    ? `${IMAGE_URL}/${ctx.logoUrl}`
    : "";

  const fmt = (v: number) =>
    (v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const currency = "LKR";

  const contentContext = {
    ...ctx,
    logoUrl,
    customerName: ctx.customerName || "Walk-in Customer",
    footer: ctx.footer || "Thank you for your business!",
    codAmount: ctx.codAmount || 0,
    paidAmountFormatted: `${currency} ${fmt(ctx.paidAmount)}`,
    codAmountFormatted: `${currency} ${fmt(ctx.codAmount)}`,
    courierName: ctx.courierName || "",
    courierPhone: ctx.courierPhone || "",
    isA4: ctx.pageSize === "A4" || ctx.pageSize === "A3",
  };

  const content = renderTemplate(COURIER_PARCEL_TEMPLATE, contentContext);
  const styles = buildCssVariables(ctx.pageSize) + "\n" + COURIER_PARCEL_CSS;

  return renderTemplate(BASE_TEMPLATE, {
    title: `Courier Parcel - ${ctx.orderNo}`,
    styles,
    content,
  });
}
